import { doc, DocumentSnapshot } from "firebase/firestore";
import { db } from "@poseidon/firebase/firebase";
import config from "@poseidon/utils/config";
import { camelToSentenceCase } from "@poseidon/utils/text";
import { useEffect, useState } from "react";
import { useDocument } from "react-firebase-hooks/firestore";
import {
  AffordabilityGridRow,
  affordabilitySection,
  isAffordable,
  Sections,
} from "../pages/creditUnderwriting/affordabilitySections";
import { AffordabilityReport } from "../pages/creditUnderwriting/affordabilityState";
import { affordabilityReportSchema } from "../schema";

const order = [
  "income-heading",
  "income-mainApplicantIncome",
  "income-mainApplicantOtherIncome",
  "income-totals",
  "currentMonthlyCommitments-heading",
  "currentMonthlyCommitments-mortgageRent",
  "currentMonthlyCommitments-totalLoans",
  "currentMonthlyCommitments-outstandingCreditCardBalances",
  "currentMonthlyCommitments-outstandingBNPLBalances",
  "currentMonthlyCommitments-otherCommittedExpenses",
  "currentMonthlyCommitments-totals",
  "monthlyLivingCosts-heading",
  "monthlyLivingCosts-numberOfDependants",
  "monthlyLivingCosts-onsGeneratedLivingCosts",
  "monthlyLivingCosts-totals",
  "newBoatCosts-heading",
  "newBoatCosts-monthlyLoanPayment",
  "newBoatCosts-monthlyRunningCosts",
  "newBoatCosts-totals",
  "workings-heading",
  "workings-totalExpenditure",
  "workings-inflationBuffer",
  "workings-totalAssessedExpenditure",
  "workings-netSurplus",
  "workings-affordabilityBuffer",
  "workings-assessedNetSurplus",
  "isAffordable",
];

const titles: Record<string, string> = {
  mainApplicantOtherIncome: "Main Applicant Other Income",
  mainApplicantIncome: "Main Applicant Income",
  income: "Income",
  "income-totals": "Total Income",
  totalLoans: "Total Loans",
  mortgageRent: "Mortgage/Rent",
  outstandingCreditCardBalances: "Outstanding Credit Card Balances (5%)",
  outstandingBNPLBalances: "Outstanding BNPL Balances (5%)",
  otherCommittedExpenses: "Other Committed Expenses",
  currentMonthlyCommitments: "Current Monthly Commitments",
  "currentMonthlyCommitments-totals": "Total Current Monthly Commitments",
  numberOfDependants: "Number Of Dependants",
  onsGeneratedLivingCosts: "ONS Generated Living Costs",
  monthlyLivingCosts: "Monthly Living Costs",
  "monthlyLivingCosts-totals": "Total Monthly Living Costs",
  monthlyLoanPayment: "Monthly Loan Payment",
  monthlyRunningCosts: "Monthly Running Costs (5%)",
  newBoatCosts: "New Boat Costs",
  "newBoatCosts-totals": "Total Boat Costs",
  assessedNetSurplus: "Assessed Net Surplus",
  totalAssessedExpenditure: "Total Assessed Expenditure",
  inflationBuffer: "Inflation Buffer (5% of total expenditure)",
  netSurplus: "Net Surplus",
  affordabilityBuffer: "Affordability Buffer (15% of total income)",
  totalExpenditure: "Total Expenditure",
  workings: "Workings",
  isAffordable: "Is Affordable",
};
const getTitle = (key: string) => {
  return titles[key] ?? camelToSentenceCase(key);
};

export const useAffordabilityReport = (applicationId: string) => {
  const [gridRows, setGridRows] = useState<AffordabilityGridRow[]>([]);
  const [affordabilityReport, setAffordabilityReport] =
    useState<AffordabilityReport | null>();

  const { SaltCollection } = config;

  const [value, loading, error] = useDocument(
    doc(
      db,
      SaltCollection.Application,
      applicationId,
      SaltCollection.Application_AffordabilityReport,
      SaltCollection._current
    ),
    {
      snapshotListenOptions: { includeMetadataChanges: true },
    }
  );

  useEffect(() => {
    if (value) {
      const affordabilityReport = mapAffordabilityReport(value);
      setAffordabilityReport(affordabilityReport);
      if (!affordabilityReport) {
        return;
      }
      const mapped = mapGridRows(affordabilityReport);
      setGridRows(mapped);
    }
  }, [value, applicationId]);

  const mapGridRows = (affordabilityReport: AffordabilityReport) => {
    const declared = affordabilityReport.declared;
    const manual = affordabilityReport.manual;
    const openBanking = affordabilityReport.openBanking;
    const variance = affordabilityReport.variance;
    const rows = affordabilitySection.map((key: Sections) => {
      const declaredInner = declared[key] as any;
      const manualInner = (manual ? manual[key] : false) as any;
      const openBankingInner = (openBanking ? openBanking[key] : false) as any;
      const varianceInner = (variance ? variance[key] : false) as any;

      const innerRows: AffordabilityGridRow[] = Object.keys(declaredInner)
        .filter((innerKey) => {
          return !(
            !affordabilityReport.meta.applicationIsJoint &&
            innerKey.includes("joint")
          );
        })
        .map((innerKey): AffordabilityGridRow => {
          return {
            id: `${key}-${innerKey}`,
            key: innerKey,
            section: key,
            rowType: "inner",
            title: getTitle(innerKey),
            declared: declaredInner[innerKey],
            manual: manualInner[innerKey] ?? 0,
            openBanking: openBankingInner[innerKey] ?? null,
            variance: varianceInner[innerKey] ?? null,
          };
        });

      return [
        {
          id: `${key}-heading`,
          section: key,
          section2: key,
          rowType: "heading",
          key: key,
          title: getTitle(key),
        },
        ...innerRows,
        ...(key === "workings"
          ? []
          : [
              {
                id: `${key}-totals`,
                key: `${key}-totals`,
                title: getTitle(`${key}-totals`),
                section: key,
                rowType: "total",
                declared: declared.totals[key],
                manual: (manual?.totals && manual.totals[key]) ?? 0,
                openBanking:
                  (openBanking?.totals && openBanking.totals[key]) ?? null,
                variance: (variance?.totals && variance.totals[key]) ?? null,
              },
            ]),
      ];
    });
    return [
      ...rows.flat(),
      {
        id: `${isAffordable}`,
        key: isAffordable,
        section: isAffordable,
        rowType: "result",
        title: getTitle(isAffordable),
        declared: affordabilityReport.declared.isAffordable,
        manual: affordabilityReport.manual?.isAffordable,
        openBanking: affordabilityReport.openBanking?.isAffordable,
        variance: affordabilityReport.variance?.isAffordable,
      },
    ].sort((a, b) => {
      const orderA = order.indexOf(a.id);
      const orderB = order.indexOf(b.id);
      return orderA - orderB;
    });
  };
  return { affordabilityReport, gridRows, loading, error };
};

const mapAffordabilityReport = (snapshot: DocumentSnapshot) => {
  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data();
  return affordabilityReportSchema.cast(data);
};
